import React from 'react';
import clsx from 'clsx';
import Flexbox from '@/components/common/Flexbox';
import Image from 'next/image';
import { AgentEllenImage } from '@/public/assets/images';
import { BsLine } from 'react-icons/bs';
import { LineEventTypes, sendLineTagEvent } from '@/utils/sendLineTagEvent';
import { Button } from '@chakra-ui/react';
import { PhoneIcon } from '@chakra-ui/icons';
import Link from 'next/link';
import ReactIcon from '@/src/components/common/ReactIcon';

const AgentSection: React.FC<{
  sid: string;
  title: string;
  className?: string;
  contactFormPath?: string;
  ctaText?: string;
  ctaAction?: 'call' | 'link';
}> = ({
  sid,
  title,
  className,
  contactFormPath = '/abroad#contact-us',
  ctaText = '立即諮詢',
  ctaAction = 'link',
}) => {
  const consultantTitle = '客戶經理｜Ellen';
  const onCallClick = () => sendLineTagEvent(`${LineEventTypes.phone_call}_${sid}`);
  // oaMessage 深連結只有在行動裝置環境下才能正確開啟 LINE App；桌面瀏覽器會被導回 line.me 首頁，
  // 因此桌面版改用固定的官方帳號連結（見 design.md 決策 4 的「修正（第二輪審核回饋）」）。
  const lineHrefMobile = `https://line.me/R/oaMessage/@funwoo/?${encodeURIComponent(`我想詢問 ${sid} ${title}`)}`;
  const lineHrefDesktop = 'https://line.me/ti/p/@funwoo';
  const onLineClick = () => sendLineTagEvent(`${LineEventTypes.add_line}_OA`);

  return (
    <div className={clsx(className)}>
      <div className={clsx('border border-gray300')}>
        <Flexbox align={'center'} className={clsx('p-4', 'space-x-6')}>
          <Flexbox
            align={'center'}
            justify={'center'}
            className={clsx('relative', 'w-20 h-20', 'rounded-full', 'overflow-hidden')}
          >
            <Image src={AgentEllenImage} alt={'Ellen'} width={80} height={80} objectFit={'cover'} />
          </Flexbox>
          <div className={clsx('flex-1', 'py-3', 'space-y-2.5')}>
            <p className={clsx('font-medium text-lg')}>{consultantTitle}</p>
            <Flexbox align={'center'} className={clsx('py-2', 'space-x-2', 'text-lg leading-8 font-medium', 'text-brand')}>
              <a
                href={`tel:${process.env.NEXT_PUBLIC_FUNWOO_TAIWAN_CELLPHONE},888`}
                className={clsx('flex-1', 'flex flex-wrap items-baseline', 'gap-x-1')}
              >
                <span className={clsx('whitespace-nowrap')}>服務專線</span>
                <span className={clsx('whitespace-nowrap')}>{process.env.NEXT_PUBLIC_FUNWOO_TAIWAN_CELLPHONE} #888</span>
              </a>
              <a
                href={lineHrefMobile}
                target={'_blank'}
                rel={'noopener noreferrer'}
                onClick={onLineClick}
                className={clsx('sm:hidden', 'flex items-center justify-center', 'w-9 h-9', 'text-gray900')}
              >
                <ReactIcon icon={BsLine} size={24} />
              </a>
              <a
                href={lineHrefDesktop}
                target={'_blank'}
                rel={'noopener noreferrer'}
                onClick={onLineClick}
                className={clsx('hidden sm:flex', 'items-center justify-center', 'sm:w-12 sm:h-12', 'text-gray900')}
              >
                <ReactIcon icon={BsLine} size={24} />
              </a>
            </Flexbox>
          </div>
        </Flexbox>
        <Flexbox className={clsx('hidden sm:flex', 'px-4 pb-6', 'space-x-4')}>
          <div className={clsx('flex-1')}>
            {ctaAction === 'call' ? (
              <Button
                as={'a'}
                href={`tel:${process.env.NEXT_PUBLIC_FUNWOO_TAIWAN_CELLPHONE}`}
                leftIcon={<PhoneIcon />}
                w={'full'}
                h={12}
                onClick={onCallClick}
              >
                {ctaText}
              </Button>
            ) : (
              <Link href={contactFormPath} passHref>
                <Button as={'a'} leftIcon={<PhoneIcon />} w={'full'} h={12}>
                  {ctaText}
                </Button>
              </Link>
            )}
          </div>
          <div className={clsx('flex-1')}>
            <Link href={'/contact'} passHref>
              <Button as={'a'} variant={'secondary'} w={'full'} h={12}>
                專人回電
              </Button>
            </Link>
          </div>
        </Flexbox>
      </div>
    </div>
  );
};

export default AgentSection;
